/**
 * Readers for the session-held facts the beta home page needs beyond the
 * snapshot bundle: the Secure Your Future enrollment, the Financial Wellness
 * train and the loan-repayment plan. Each takes the raw record or payload and
 * returns a plain object, so `home-model.ts` stays a pure function of plain data.
 */
import { parseDate } from 'tio-employee/utils/beta/home-model';
import type {
  BetaPlanSignals,
  BetaSyfSignals,
  BetaWellnessSignals,
  BetaWellnessScore,
} from 'tio-employee/utils/beta/home-model';

const DAY_MS = 86_400_000;

/** The last instant of a date-only deadline's calendar day, in local time. */
function endOfDay(isoDate: string): number | null {
  const start = parseDate(isoDate);
  return start === null ? null : start + DAY_MS - 1;
}

/** The slice of a match participant this page reads; the live record is a `MatchParticipantModel`. */
export interface MatchParticipantLike {
  isEnrolled?: boolean;
  isEnrolling?: boolean;
  enrollmentDate?: string;
  matchPlan?: {
    metadata?: {
      benefit_years?: Record<
        string,
        { benefit_year: number | string; final_certification_date: Date | string }
      >;
    };
  } | null;
}

/**
 * Reads the person's Secure Your Future state off the session's match participant.
 * The plan year is the earliest one whose final certification date has not passed,
 * the same rule the SYF dashboard uses to pick its default year. A date-only
 * deadline stays current through the whole of its calendar day: read as UTC
 * midnight it would lapse the evening before in US time zones.
 *
 * @param participant the employee's match participant, or nothing when SYF is not set up
 * @param now the current time in epoch milliseconds
 * @returns enrollment state and the current plan year's certification deadline, or null when there is no participant
 */
export function syfSignals(
  participant: MatchParticipantLike | null | undefined,
  now: number
): BetaSyfSignals | null {
  if (!participant) return null;
  const years = Object.values(participant.matchPlan?.metadata?.benefit_years ?? {})
    .map((year) => ({
      benefitYear: String(year.benefit_year),
      deadline: toIsoDate(year.final_certification_date),
    }))
    .filter((year): year is { benefitYear: string; deadline: string } => year.deadline !== null)
    .sort((a, b) => a.deadline.localeCompare(b.deadline));
  const current = years.find((year) => (endOfDay(year.deadline) ?? -Infinity) >= now) ?? null;

  return {
    enrollment: participant.isEnrolled
      ? 'enrolled'
      : participant.isEnrolling
        ? 'enrolling'
        : 'none',
    certificationDeadline: current?.deadline ?? null,
    benefitYear: current?.benefitYear ?? null,
    enrolledAt: participant.enrollmentDate ?? null,
  };
}

function toIsoDate(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }
  return Number.isNaN(Date.parse(value)) ? null : value;
}

const SCORE_BY_COMPONENT: Record<string, BetaWellnessScore> = {
  score_on_track: 'on-track',
  score_need_adjustment: 'needs-adjustment',
  score_need_attention: 'needs-attention',
};

/**
 * Reads the Financial Wellness train off the authenticated route's raw user-profile
 * payload, which is flat and snake_case exactly as tio-api returns it.
 *
 * @param payload the raw `wellness_train` profile value, or nothing when it has not been calculated
 * @returns the score, its dates and whether loans are linked, or null without a payload
 */
export function wellnessSignals(
  payload: Record<string, unknown> | null | undefined
): BetaWellnessSignals | null {
  if (!payload) return null;
  const components = Array.isArray(payload['components'])
    ? (payload['components'] as { type?: string; name?: string }[])
    : [];
  const scoreComponent = components.find((component) => component.type === 'score');
  const score = scoreComponent?.name ? (SCORE_BY_COMPONENT[scoreComponent.name] ?? null) : null;

  return {
    score,
    asOf: isoOrNull(payload['calculation_updated_at']) ?? isoOrNull(payload['obs_updated_at']),
    balanceAsOf: isoOrNull(payload['balance_updated_at']) ?? isoOrNull(payload['obs_updated_at']),
    nsldsAsOf: isoOrNull(payload['nslds_as_of']),
    hasLinkedLoans: payload['has_linked_loans'] === true,
  };
}

function isoOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 && !Number.isNaN(Date.parse(value))
    ? value
    : null;
}

/**
 * Reads the loan-repayment plan off the session employee. `annualLimit` is cents
 * on the wire, as every SLR surface divides it.
 *
 * @param plan the employee's plan record, or nothing
 * @returns the annual limit in dollars and the contribution cadence, or null without a plan
 */
export function planSignals(
  plan: { annualLimit?: number | null; cadence?: string | null } | null | undefined
): BetaPlanSignals | null {
  if (!plan) return null;
  return {
    annualLimit:
      typeof plan.annualLimit === 'number' && plan.annualLimit > 0 ? plan.annualLimit / 100 : null,
    cadence: plan.cadence ?? null,
  };
}
